import { useMemo } from 'react';

import { useQuery } from '@apollo/client';
import { billingTypes } from '@coursera-web/wes-common';
import * as Sentry from '@sentry/react';
import URI from 'jsuri';
import { includes } from 'lodash';
import Q from 'q';

import API from 'js/lib/api';

import type {
  GetRolesByUserRequest,
  GetRolesByUserResponse,
} from '@coursera/grpc-types-enterprisepermissions/coursera/proto/enterprisepermissions/userroles/v1beta1/enterprise_user_roles_api';

import { enterpriseOrganizationIdsQuery } from 'bundles/enterprise-admin-navigation/utils/withManagedOrganizations';
import EnterpriseExperiments from 'bundles/epic/clients/Enterprise';
import type { Invoice } from 'bundles/internal-enterprise/components/lead/EnterpriseLeadContainer';
import type {
  CreditCardContractPriceQuery,
  CreditCardContractPriceQueryVariables,
} from 'bundles/internal-enterprise/components/lead/__generated__/CreditCardContractPriceQuery';
import type {
  UserByEmailQuery,
  UserByEmailQueryVariables,
} from 'bundles/internal-enterprise/components/lead/__generated__/UserByEmailQuery';
import {
  creditCardContractPriceQuery,
  thirdPartyOrganizationsQuery,
  userByEmailQuery,
} from 'bundles/internal-enterprise/components/lead/queries';
import computeInvoiceBilledContractPriceQuery from 'bundles/internal-enterprise/components/lead/queries/ComputeInvoiceBilledContractPrice.graphql';
import EnterpriseContractInvoice_CreateInvoiceOrder from 'bundles/internal-enterprise/components/lead/queries/EnterpriseContractInvoice_CreateInvoiceOrder.graphql';
import type {
  ComputeInvoiceBilledContractPriceQuery,
  ComputeInvoiceBilledContractPriceQueryVariables,
} from 'bundles/internal-enterprise/components/lead/queries/__generated__/ComputeInvoiceBilledContractPrice';
import type {
  EnterpriseContractInvoice_CreateInvoiceOrderMutation,
  EnterpriseContractInvoice_CreateInvoiceOrderMutationVariables,
} from 'bundles/internal-enterprise/components/lead/queries/__generated__/EnterpriseContractInvoice_CreateInvoiceOrder';
import type {
  ThirdPartyOrgByIdsQuery,
  ThirdPartyOrgByIdsQueryVariables,
} from 'bundles/internal-enterprise/components/lead/queries/__generated__/ThirdPartyOrgByIds';
import useGqlQuery from 'bundles/internal-enterprise/components/lead/useGqlQuery';
import usePowerUser from 'bundles/internal-enterprise/components/lead/usePowerUser';
import { useNaptime } from 'bundles/naptimejs';
import EnterpriseProgramsV1 from 'bundles/naptimejs/resources/enterprisePrograms.v1';
import { createApolloClient } from 'bundles/page/lib/network/Apollo';

import type {
  EnterpriseContractInvoice_OrganizationType,
  EnterpriseContractInvoice_SalesOwner,
} from '../../../../__generated__/graphql-types';

/**
 * Given user email, fetch the user id.
 * @param email User email.
 * @returns
 */
export const useUserId = (email?: string) => {
  const userResult = useGqlQuery<UserByEmailQuery, UserByEmailQueryVariables>(userByEmailQuery, {
    variables: { email: email ?? '' },
    skip: !email,
  });

  const userId = userResult?.data.UsersV1Resource.email?.elements?.[0]?.id;
  return userId;
};

/**
 * Given user email, fetch the user id and the organizations that the user manages.
 * @param email User email.
 * @returns
 */
export const useUserInfo = (email?: string) => {
  // This function used three graphql queries in serial, each depending on the previous result
  // (the first query depends on the input email).
  // So failed query will stop the query chain.

  const userId = useUserId(email);

  const { data: rolesData } = useQuery<{ getRolesByUser: GetRolesByUserResponse }, { input: GetRolesByUserRequest }>(
    enterpriseOrganizationIdsQuery,
    {
      variables: { input: { userId } },
      skip: !userId,
    }
  );
  const organizationIds =
    rolesData?.getRolesByUser?.roles.map((role) => role.roleDomainId?.organizationId ?? '').filter((id) => id) ?? [];
  const programIds =
    rolesData?.getRolesByUser?.roles.map((role) => role.roleDomainId?.programId ?? '').filter((id) => id) ?? [];

  const {
    data: { programs = [] },
  } = useNaptime<{ programs: { thirdPartyOrganizationId: string }[] }>(() => ({
    programs:
      programIds.length > 0
        ? EnterpriseProgramsV1.multiGet(programIds, {
            fields: ['id', 'thirdPartyOrganizationId'],
            required: false,
          })
        : [],
  }));

  const orgIds = Array.from(new Set([...organizationIds, ...programs.map((_) => _.thirdPartyOrganizationId)]));

  const orgsResult = useGqlQuery<ThirdPartyOrgByIdsQuery, ThirdPartyOrgByIdsQueryVariables>(
    thirdPartyOrganizationsQuery,
    {
      variables: {
        ids: orgIds,
      },
      skip: orgIds.length === 0,
    }
  );

  const orgs = orgsResult?.data.ThirdPartyOrganization?.queryByIds ?? [];

  return { userId, orgs };
};

export const isSeatsValid = (seats: number) => seats >= 5 && seats <= 500;

// TODO: Move this to wes-common and handle other graphql calls as well

export const handleGraphqlError = (response: { errors?: readonly { message: string }[] } = {}) => {
  const hasError = response.errors && response.errors.length > 0 && response.errors[0]?.message;
  if (hasError) {
    const message = response.errors?.[0]?.message || 'GraphQL API FAILURE';
    Sentry.captureException(new Error(message), {
      extra: { errors: response.errors?.[0] },
    });
    return true;
  }
  return false;
};

/**
 * Compute invoice total amount
 */
export const useInvoicePrice = (seats: number, countryCode?: string, customerUserId?: number) => {
  const shouldRun = isSeatsValid(seats) && !!countryCode && !!customerUserId;

  const queryResult = useGqlQuery<
    ComputeInvoiceBilledContractPriceQuery,
    ComputeInvoiceBilledContractPriceQueryVariables
  >(computeInvoiceBilledContractPriceQuery, {
    variables: {
      billingType: billingTypes.ONE_TIME,
      countryIsoCode: countryCode as string,
      userLimit: seats,
      userId: String(customerUserId),
    },
    skip: !shouldRun,
  });

  const invoiceBilledContractPrice =
    queryResult?.data?.EnterpriseContractPriceQueries?.computeInvoiceBilledContractPrice?.elements?.[0];

  if (shouldRun) {
    handleGraphqlError(queryResult);
  }

  const normalized = useMemo(() => {
    if (!shouldRun) return undefined;
    if (!invoiceBilledContractPrice) return undefined;
    const price = invoiceBilledContractPrice?.price;
    if (!price) return undefined;
    return {
      unitPrice: String(price.unitPrice),
      amountTotal: String(price.amountTotal),
      currencyCode: price.currencyCode,
    } as const;
  }, [shouldRun, invoiceBilledContractPrice]);

  return normalized;
};

/**
 * Fetch the unit price for credit card payment. (geo-pricing and discounts not included)
 */
export const useCreditCardUnitPrice = () => {
  const result = useGqlQuery<CreditCardContractPriceQuery, CreditCardContractPriceQueryVariables>(
    creditCardContractPriceQuery,
    { variables: { seats: 1 } }
  );

  return result?.data.EnterpriseSelfServeContractProductPricesV1Resource.getEnterpriseSelfServeContractProductPrice
    ?.elements?.[0]?.amountTotal;
};

/**
 * Creates an invoice order for enterprise contracts.
 *
 * MIGRATION NOTE: This function supports both REST API and GraphQL mutation based on feature flag.
 * After rollout is complete and REST API is deprecated, cleanup steps:
 * 1. Remove the feature flag check and REST API implementation
 * 2. Remove REST API imports: `API` from 'js/lib/api' and `URI` from 'jsuri'
 * 4. Simplify error handling to remove REST API error format transformation
 * 5. Update tests to remove REST API mocks and feature flag toggling
 *
 * @param invoice - Invoice data containing all order details
 * @param useSandbox - Whether to use sandbox environment
 * @returns Promise resolving to the created invoice order data
 */
export const createInvoice = (invoice: Invoice, useSandbox = false) => {
  const isInvoiceOrderGraphqlEnabled = EnterpriseExperiments.get('enterpriseContractInvoiceOrdersGraphql');

  if (isInvoiceOrderGraphqlEnabled) {
    const client = createApolloClient();

    const input: EnterpriseContractInvoice_CreateInvoiceOrderMutationVariables['input'] = {
      numberOfLicenses: invoice.seats,
      adminBillingEmail: invoice.customerEmail,
      adminTitle: invoice.role,
      organizationId: invoice.existingOrg?.value,
      organizationName: invoice.company,
      organizationType: invoice.organizationType as EnterpriseContractInvoice_OrganizationType,
      organizationBillingAddress: {
        line1: invoice.address1,
        line2: invoice.address2 ?? '',
        city: invoice.city,
        state: invoice.state,
        country: invoice.country?.label ?? '',
        postalCode: invoice.postalCode,
      },
      billingCountryCode: invoice.country?.value ?? '',
      salesOwner: invoice.owner as EnterpriseContractInvoice_SalesOwner,
      taxIdData:
        invoice.taxId && invoice.taxIdTypeCode
          ? {
              taxIdValue: invoice.taxId,
              taxIdType: invoice.taxIdTypeCode,
            }
          : undefined,
      poNumber: invoice.poNumber,
      useSandbox,
    };

    return Q(
      client
        .mutate<
          EnterpriseContractInvoice_CreateInvoiceOrderMutation,
          EnterpriseContractInvoice_CreateInvoiceOrderMutationVariables
        >({
          mutation: EnterpriseContractInvoice_CreateInvoiceOrder,
          variables: { input },
          context: { clientName: 'gatewayGql' },
        })
        .then((result) => {
          // Handle GraphQL errors - GraphQL can return status 200 with errors array
          if (result.errors && result.errors.length > 0) {
            const errorMessage = result.errors[0]?.message || 'Failed to create invoice order';
            throw new Error(errorMessage);
          }
          return result.data;
        })
        .catch((error) => {
          // Re-throw with simple error message
          const errorMessage = error?.message || 'Failed to create invoice order';
          throw new Error(errorMessage);
        })
    );
  }

  // TODO: REMOVE AFTER ROLLOUT - REST API implementation below will be deleted
  // This block (lines 289-327) should be removed once GraphQL migration is complete
  // and the feature flag 'enterpriseContractInvoiceOrdersGraphql' is removed
  // Use REST API (existing implementation - to be removed)
  const data = {
    numberOfLicenses: invoice.seats,
    adminBillingEmail: invoice.customerEmail,
    adminTitle: invoice.role,
    organizationId: invoice.existingOrg?.value,
    organizationName: invoice.company,
    organizationType: invoice.organizationType,
    organizationBillingAddress: {
      line1: invoice.address1,
      line2: invoice.address2,
      city: invoice.city,
      state: invoice.state,
      country: invoice.country?.label,
      postalCode: invoice.postalCode,
    },
    billingCountryCode: invoice.country?.value,
    salesOwner: invoice.owner,
    taxIdData:
      invoice.taxId && invoice.taxIdTypeCode
        ? {
            taxId: invoice.taxId,
            taxIdType: invoice.taxIdTypeCode,
          }
        : undefined,
    poNumber: invoice.poNumber,

    // avoid filling the request with `useSandbox: false`
    ...(useSandbox ? { useSandbox } : {}),
  };

  const api = API('/api/', { type: 'rest' });
  const uri = new URI('enterpriseContractInvoiceOrders.v1');
  return Q(
    api.post(uri.toString(), {
      data,
    })
  );
};

export const useNamerOrEmeaRoles = (userId: number) => {
  const powerUser = usePowerUser(userId);
  if (!powerUser) {
    return false;
  }

  if (
    includes(powerUser.assignedRoleKeys, 'enterprise_sales_namer') ||
    includes(powerUser.assignedRoleKeys, 'enterprise_sales_emea')
  ) {
    return true;
  }

  return false;
};
